import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Subject, Observable, of } from "rxjs";
import { map, catchError } from 'rxjs/operators';
import { Router } from "@angular/router";

import { Player, RosterRole } from "./player.model";
import { environment } from "../../environments/environment";

const BACKEND_URL = environment.apiUrl + "/players/";

export interface FormerRosterPlayer {
  id: string;
  name: string;
  jersey: number | null;
  profileImage: string | null;
  removedAt: string;
}

export interface FormerTeamStaffMember {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  removedAt: string;
}

function normalizeApiRosterRole(role: unknown): RosterRole {
  const normalized = String(role || "player").toLowerCase();
  return normalized === "coach" || normalized === "assistant_coach" || normalized === "general_manager"
    ? normalized
    : "player";
}

function mapApiPlayer(player: any): Player {
  return {
    id: player._id,
    fName: player.fName,
    lName: player.lName,
    jersey: player.jersey,
    size: player.size,
    phone: player.phone,
    email: player.email,
    ig: player.ig,
    gender: player.gender,
    birthdate: player.birthdate ? new Date(player.birthdate) : undefined,
    accountPurpose: player.accountPurpose ?? player.account_purpose,
    identityVerification: player.identityVerification,
    role: player.role ?? player.userRole ?? "user",
    emailVerified: !!player.emailVerified,
    hasAccount: !!(player.authUserId ?? player.hasAccount),
    player_img_url: player.player_img_url,
    profileImage: player.profileImage ?? null,
    rosterRole: normalizeApiRosterRole(player.rosterRole ?? player.roster_role),
    feeExempt: !!(player.feeExempt ?? player.fee_exempt),
    currentTeams: Array.isArray(player.currentTeams) ? player.currentTeams : [],
  };
}

@Injectable({ providedIn: "root" })
export class PlayersService {
  private players: Player[] = [];
  private playersUpdated = new Subject<{
    players: Player[];
    total: number;
    roleCounts?: Record<string, number>;
  }>();

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  getPlayers(
    pageSize: number,
    currentPage: number,
    teamId?: string,
    excludeRosterTeamId?: string,
    search?: string,
    accountOnly?: boolean,
    role?: string
  ) {
    let queryParams = `?pagesize=${pageSize}&page=${currentPage}`;
    if (teamId) {
      queryParams += `&teamId=${teamId}`;
    }
    if (excludeRosterTeamId) {
      queryParams += `&excludeRosterTeamId=${excludeRosterTeamId}`;
    }
    const trimmedSearch = search?.trim();
    if (trimmedSearch) {
      queryParams += `&search=${encodeURIComponent(trimmedSearch)}`;
    }
    if (accountOnly) {
      queryParams += `&accountOnly=true`;
    }
    const trimmedRole = role?.trim();
    if (trimmedRole) {
      queryParams += `&role=${encodeURIComponent(trimmedRole)}`;
    }
    const url = BACKEND_URL + queryParams;
    this.http
      .get<{ message: string; players: any; total: number; roleCounts?: Record<string, number> }>(url)
      .pipe(map((playerData) => {
        return {
          players: playerData.players.map((player) => mapApiPlayer(player)),
          total: playerData.total,
          roleCounts: playerData.roleCounts,
        }
        ;
      }))
      .subscribe(transformedPlayers => {        
        this.players = transformedPlayers.players;
        this.playersUpdated.next({
          players: [...this.players],
          total: transformedPlayers.total,
          roleCounts: transformedPlayers.roleCounts,
        });
      });
  }

  /**
   * GET /players/unassigned — players eligible to add to the given team.
   * Excludes anyone already rostered in the same season + division; players on
   * other divisions remain available. Pass `teamId` for scoped results and auth.
   */
  getPlayersWithoutTeam(pageSize: number, currentPage: number, teamId?: string, accountOnly = false) {
    let queryParams = `?pagesize=${pageSize}&page=${currentPage}`;
    if (teamId) {
      queryParams += `&teamId=${encodeURIComponent(teamId)}`;
    }
    if (accountOnly) {
      queryParams += `&accountOnly=true`;
    }
    const url = BACKEND_URL + "unassigned" + queryParams;
    this.http
      .get<{ message: string; players: any; total: number }>(url)
      .pipe(
        map((playerData) => ({
          players: playerData.players.map((player) => mapApiPlayer(player)),
          total: playerData.total,
        }))
      )
      .subscribe((transformedPlayers) => {
        this.players = transformedPlayers.players;
        this.playersUpdated.next({
          players: [...this.players],
          total: transformedPlayers.total,
        });
      });
  }

  getPlayer(id: string) {
    return this.http.get<{ 
      _id: string;
      fName: string;
      lName: string;
      jersey: number;
      size: string;
      phone: string;
      email: string;
      ig: string;
      gender: string;
      birthdate?: string;
    }>(
      BACKEND_URL + id
    );
  }

  getPlayerUpdateListener() {
    return this.playersUpdated.asObservable();
  }

  /**
   * Loads one team roster page as an Observable; field visibility follows the API for the current session.
   */
  getPlayersPage(
    pageSize: number,
    page: number,
    teamId: string
  ): Observable<{ players: Player[]; total: number }> {
    const queryParams = `?pagesize=${pageSize}&page=${page}&teamId=${encodeURIComponent(teamId)}`;
    return this.http
      .get<{ message: string; players: any[]; total: number }>(BACKEND_URL + queryParams)
      .pipe(
        map((playerData) => ({
          players: playerData.players.map((player) => mapApiPlayer(player)),
          total: playerData.total,
        }))
      );
  }

  getRosterCandidates(teamId: string, accountOnly = true): Observable<{ players: Player[]; total: number }> {
    let params = new HttpParams()
      .set("pagesize", "99999")
      .set("page", "1")
      .set("teamId", teamId);
    if (accountOnly) params = params.set("accountOnly", "true");

    return this.http
      .get<{ players: any[]; total: number }>(BACKEND_URL + "unassigned", { params })
      .pipe(map((data) => ({
        players: data.players.map((player) => mapApiPlayer(player)),
        total: data.total,
      })));
  }

  /**
   * Active roster counts for many teams in one request (`POST /players/roster-counts`).
   * Response `counts` is keyed by team id string.
   */
  getTeamRosterCounts(teamIds: string[]): Observable<Record<string, number>> {
    if (!teamIds.length) {
      return of({});
    }
    return this.http
      .post<{ counts: Record<string, number> }>(`${BACKEND_URL}roster-counts`, {
        teamIds,
      })
      .pipe(
        map((res) => res.counts ?? {}),
        catchError(() => of({}))
      );
  }

  importPlayers(jsonData: any, teamId?: string) {
    this.http
      .post(BACKEND_URL + 'import', {dataToImport: jsonData, teamId: teamId})
      .subscribe(response => {
        console.log('import response: ', response);
        
        if (teamId) {
          this.router.navigate(['/team', teamId, 'roster']);
        }
        else {
          this.router.navigate(['/my-dashboard/admin/users']);
        }
      });
  }

  /**
   * Caller subscribes and navigates on success (e.g. add-existing UI shows its own loading state).
   */
  addExistingPlayersToTeam(
    playerIds: string[],
    teamId?: string,
    rosterRole?: RosterRole
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      BACKEND_URL + "add-existing-players-to-team",
      { playerIds, teamId, ...(rosterRole ? { rosterRole } : {}) }
    );
  }

  updateRosterMemberRole(
    teamId: string,
    playerId: string,
    rosterRole: RosterRole
  ): Observable<{ message: string; rosterRole: RosterRole }> {
    return this.http.put<{ message: string; rosterRole: RosterRole }>(
      BACKEND_URL + "roster-role",
      { teamId, playerId, rosterRole }
    );
  }

  updateRosterFeeExempt(
    teamId: string,
    playerId: string,
    feeExempt: boolean
  ): Observable<{ message: string; feeExempt: boolean }> {
    return this.http.put<{ message: string; feeExempt: boolean }>(
      BACKEND_URL + "roster-fee-exempt",
      { teamId, playerId, feeExempt }
    );
  }

  updateRosterProfilePhoto(
    teamId: string,
    playerId: string,
    file: File
  ): Observable<{ player: Player }> {
    const form = new FormData();
    form.append("teamId", teamId);
    form.append("profilePicture", file);
    return this.http.patch<{ player: any }>(
      `${BACKEND_URL}${playerId}/roster-profile`,
      form
    ).pipe(
      map((res) => ({ player: mapApiPlayer(res.player) }))
    );
  }

  updateRosterJersey(
    teamId: string,
    playerId: string,
    jersey: number | null
  ): Observable<{ message: string; jersey: number | null }> {
    return this.http.patch<{ message: string; jersey: number | null }>(
      `${BACKEND_URL}${playerId}/roster-profile`,
      { teamId, jersey }
    );
  }

  removeRosterProfilePhoto(
    teamId: string,
    playerId: string
  ): Observable<{ player: Player }> {
    const form = new FormData();
    form.append("teamId", teamId);
    form.append("removePhoto", "true");
    return this.http.patch<{ player: any }>(
      `${BACKEND_URL}${playerId}/roster-profile`,
      form
    ).pipe(
      map((res) => ({ player: mapApiPlayer(res.player) }))
    );
  }

  createRosterInvitation(teamId: string, email: string): Observable<{ message: string; expiresAt: string }> {
    return this.http.post<{ message: string; expiresAt: string }>(
      BACKEND_URL + "roster-invitations",
      { teamId, email }
    );
  }

  getTeamShareInviteLinkStatus(
    teamId: string,
    rosterRole: RosterRole = "player"
  ): Observable<{ active: boolean; expiresAt?: string; inviteUrl?: string; rosterRole?: RosterRole }> {
    const params = new HttpParams().set("teamId", teamId).set("rosterRole", rosterRole);
    return this.http.get<{ active: boolean; expiresAt?: string; inviteUrl?: string; rosterRole?: RosterRole }>(
      BACKEND_URL + "share-invite-link",
      { params }
    );
  }

  createTeamShareInviteLink(teamId: string, rosterRole: RosterRole = "player"): Observable<{
    message: string;
    inviteUrl: string;
    expiresAt: string;
    teamName: string;
    rosterRole: RosterRole;
  }> {
    return this.http.post<{
      message: string;
      inviteUrl: string;
      expiresAt: string;
      teamName: string;
      rosterRole: RosterRole;
    }>(BACKEND_URL + "share-invite-link", { teamId, rosterRole });
  }

  getRosterInvitation(token: string): Observable<{
    invitation: {
      inviteType: "email" | "share_link";
      rosterRole: RosterRole;
      email: string | null;
      teamId: string | null;
      teamName: string;
      status: "pending" | "accepted" | "declined" | "expired" | "revoked";
      expiresAt: string;
      hasAccount: boolean;
      alreadyOnRoster?: boolean;
    };
  }> {
    return this.http.get<{
      invitation: {
        inviteType: "email" | "share_link";
        rosterRole: RosterRole;
        email: string | null;
        teamId: string | null;
        teamName: string;
        status: "pending" | "accepted" | "declined" | "expired" | "revoked";
        expiresAt: string;
        hasAccount: boolean;
        alreadyOnRoster?: boolean;
      };
    }>(BACKEND_URL + "roster-invitations/" + encodeURIComponent(token));
  }

  acceptRosterInvitation(token: string): Observable<{
    message: string;
    teamId: string;
    identityVerificationRequired?: boolean;
    verificationStatus?: string;
  }> {
    return this.http.post<{
      message: string;
      teamId: string;
      identityVerificationRequired?: boolean;
      verificationStatus?: string;
    }>(
      BACKEND_URL + "roster-invitations/" + encodeURIComponent(token) + "/accept",
      {}
    );
  }

  declineRosterInvitation(token: string): Observable<{ message: string; teamId: string }> {
    return this.http.post<{ message: string; teamId: string }>(
      BACKEND_URL + "roster-invitations/" + encodeURIComponent(token) + "/decline",
      {}
    );
  }

  addPlayer(fName: string, lName: string, jersey: number, size: string, phone: string, email: string, ig: string, gender: string, birthdate?: Date, teamId?: string) {
    const player: Player = { 
      id: null, 
      fName: fName, 
      lName: lName ,
      jersey: jersey,
      size: size,
      phone: phone,
      email: email,
      ig: ig,
      gender: gender,
      birthdate: birthdate
    };
    this.http
      .post<{ message: string, playerId: string, total: number }>(BACKEND_URL, {player: player, teamId: teamId})
      .subscribe(responseData => {
        const id = responseData.playerId;
        if (id) {
          player.id = id;
          this.players.push(player);
          this.playersUpdated.next({players: [...this.players], total: responseData.total});
        }
        if (teamId) {
          this.router.navigate(['/team', teamId, 'roster']);
        }
      });
  }

  checkEmail(email: string, id?: string): Observable<{ exists: boolean }> {
    if (id) {
      return this.http.post<{ exists: boolean }>(`${BACKEND_URL}check-email`, {
        email: email,
        id: id
      });
    }
    else {      
      return this.http.post<{ exists: boolean }>(`${BACKEND_URL}check-email`, {
        email: email
      });
    }
  }

  updatePlayer(id: string, fName: string, lName: string, jersey: number, size: string, phone: string, email: string, ig: string, gender: string, birthdate?: Date, teamId?: string, skipRedirect?: boolean): Observable<any> {
    const player: Player = {
      id: id,
      fName: fName,
      lName: lName ,
      jersey: jersey,
      size: size,
      phone: phone,
      email: email,
      ig: ig,
      gender: gender,
      birthdate: birthdate
    };

    return this.http.put(BACKEND_URL + id, player).pipe(
      map((response) => {
        const updatedPlayers = [...this.players];
        const oldPlayerIndex = updatedPlayers.findIndex(p => p.id === player.id);
        if (oldPlayerIndex >= 0) updatedPlayers[oldPlayerIndex] = player;
        this.players = updatedPlayers;
        this.playersUpdated.next({players: [...this.players], total: [...this.players].length});
        if (!skipRedirect) {
          if (teamId) {
            this.router.navigate(['/team', teamId, 'roster']);
          } else {
            this.router.navigate(['/my-dashboard/admin/users']);
          }
        }
        return response;
      })
    );
  }

  deletePlayer(playerId: string) {
    this.http
      .put<{ message: string, total: number }>(BACKEND_URL + "delete/" + playerId, playerId)
      .subscribe((response) => {
        const updatedPlayers = this.players.filter(player => player.id !== playerId);
        this.players = updatedPlayers;
        this.playersUpdated.next({players: [...this.players], total: response.total});
      });
  }

  removePlayerFromTeam(playerId: string, teamId: string) {
    this.http
      .put<{ message: string, total: number }>(BACKEND_URL + "remove-player-from-team/" + playerId, {playerId: playerId, teamId: teamId})
      .subscribe((response) => {
        const updatedPlayers = this.players.filter(player => player.id !== playerId);
        this.players = updatedPlayers;
        this.playersUpdated.next({players: [...this.players], total: response.total});
      });
  }

  /** Same endpoint as removePlayerFromTeam, but returns an Observable for callers that need to react to success/error. */
  removeRosterMember(playerId: string, teamId: string): Observable<{ message: string; total: number }> {
    return this.http.put<{ message: string; total: number }>(
      BACKEND_URL + "remove-player-from-team/" + playerId,
      { playerId, teamId }
    );
  }

  getFormerRosterPlayers(teamId: string): Observable<{ players: FormerRosterPlayer[] }> {
    const params = new HttpParams().set("teamId", teamId);
    return this.http.get<{ players: FormerRosterPlayer[] }>(
      BACKEND_URL + "former-roster-players",
      { params }
    );
  }

  getFormerAssistantCoaches(teamId: string): Observable<{ staff: FormerTeamStaffMember[] }> {
    const params = new HttpParams().set("teamId", teamId);
    return this.http.get<{ staff: FormerTeamStaffMember[] }>(
      BACKEND_URL + "former-assistant-coaches",
      { params }
    );
  }

  restoreFormerAssistantCoach(teamId: string, playerId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      BACKEND_URL + "restore-assistant-coach",
      { teamId, playerId }
    );
  }

  restoreFormerRosterPlayer(teamId: string, playerId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      BACKEND_URL + "restore-roster-player",
      { teamId, playerId }
    );
  }
}
